import { forwardRef, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import _ from 'lodash';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CardContent from '@mui/material/CardContent';
import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import clsx from 'clsx';
import { formatBytes } from './Utils/conversors';
import ItemFile from './Children/ItemFile';

const funcPrototype = () => new Promise();

const defaultFileParams = {
  title: 'title',
};

const defaultErrEnums = {
  'too-many-files': 'Solo se permite cargar un archivo',
  'file-too-large': 'El archivo es demasiado grande',
  'unsupported-format': 'El formato de archivo no es compatible',
  'upload-failed': 'La carga del archivo ha fallado, por favor inténtelo de nuevo',
  'file-invalid-type': 'Formato de archivo no valido',
};

const defaultChunkSize = 100; // 100MB;

/**
 * Componente para seleccionar y cargar archivos, soportando carga en chunks, múltiples archivos,
 * validaciones de tamaño y tipo, y callbacks para manejo externo del estado.
 *
 * Este componente se encarga de gestionar una lista de archivos seleccionados, sus validaciones,
 * su carga al servidor (individual o en chunks), y la propagación de los resultados.
 *
 * @component
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Function} props.initialFiles - Prop para establecer el estado inicial externo con la lista actual de archivos (y su metadata).
 * @param {Object} [props.fileEqualsTo] - Objeto de referencia para determinar el campo principal (ej. `title`) en el objeto metadata.
 * @param {Function} props.storeRequest - Función async que maneja la carga del archivo al servidor.
 * @param {Function} props.deleteRequest - Función async para eliminar el archivo del servidor.
 * @param {Object} props.formDataParams - Parámetros adicionales que se agregarán al `FormData` durante la carga.
 * @param {number} [props.maxSize] - Tamaño máximo permitido por archivo (en kiloBytes).
 * @param {number} [props.maxFiles=1] - Número máximo de archivos permitidos en la selección.
 * @param {boolean} [props.uploadAsChunk=false] - Si es `true`, la carga del archivo se realiza en bloques (chunks).
 * @param {boolean} [props.disabled=false] - Si es `true`, deshabilita la selección de archivos.
 * @param {boolean} [props.readOnly=false] - Si es `true`, deshabilita la selección de archivos completamente y solo muestra una lista.
 * @param {boolean} [props.isMultiple=false] - Si es `true`, permite seleccionar múltiples archivos a la vez.
 * @param {string[]|undefined} [props.formats] - Lista de formatos MIME o extensiones permitidas (ej. `['.pdf', 'image/png']`).
 * @param {Function} [props.onMessage] - Callback para mostrar mensajes de error o éxito al usuario.
 * @param {Function} [props.onChange] - Callback que se llama cada vez que cambia la lista de archivos válidos cargados.
 * @param {number} [props.chunkSize=defaultChunkSize] - Tamaño en MB para cada chunk de archivo, si `uploadAsChunk` está activo.
 *
 * @param ref
 * @returns {JSX.Element} Elemento JSX del selector de archivos con lógica de carga integrada.
 *
 * @example
 * <FilePicker
 *   setFile={setFileHandler}
 *   storeRequest={storeFileHandler}
 *   deleteRequest={deleteFileHandler}
 *   formats={{ 'application/pdf': ['.pdf'] }}
 *   maxSize={5000}
 * />
 */
export const ChildFilePicker = forwardRef(
  (
    {
      className,
      fileEqualsTo,
      storeRequest,
      deleteRequest,
      formDataParams,
      maxSize,
      initialFiles = [],
      maxFiles = 1,
      uploadAsChunk = false,
      disabled = false,
      readOnly = false,
      isMultiple = false,
      formats,
      onMessage = () => {},
      onChange = () => {},
      chunkSize = defaultChunkSize,
      ...props
    },
    ref
  ) => {
    const itemOptions = useMemo(
      () => ({
        fileEqualsTo,
        storeRequest,
        deleteRequest,
        formDataParams,
        uploadAsChunk,
        onMessage,
        chunkSize,
      }),
      [
        fileEqualsTo,
        storeRequest,
        deleteRequest,
        formDataParams,
        uploadAsChunk,
        onMessage,
        chunkSize,
      ]
    );
    const maxItems = isMultiple ? maxFiles : 1;
    const storeWrapper = typeof storeRequest === 'function' ? storeRequest : funcPrototype;
    const deleteWrapper = typeof storeRequest === 'function' ? deleteRequest : funcPrototype;
    const [errEnums, setErrEnums] = useState(defaultErrEnums);
    const [currentErrEnum, setCurrentErrEnum] = useState([]);

    /**
     * @typedef {Object} Item
     * @property {Object|undefined} metadata - Metadatos asociados al ítem, puede ser un objeto o `undefined`.
     * @property {File|null} file - Archivo asociado, puede ser un objeto `File` o `null`.
     * @property {boolean} wasSavedToServer - Indica si el archivo fue guardado en el servidor.
     */
    /** @type {Item[]} */
    const [items, setItems] = useState([]);

    useLayoutEffect(() => {
      if (maxSize === undefined) return;
      setErrEnums((current) => {
        return {
          ...current,
          'file-too-large': `El archivo rebasa el limite de ${formatBytes(maxSize * 1024)}`,
        };
      });
    }, [maxSize, isMultiple]);

    useEffect(() => {
      if (!initialFiles || !Array.isArray(initialFiles)) return;
      if (initialFiles.length === 0) return;
      const converted = initialFiles.map((file) => ({
        wasSavedToServer: true,
        metadata: file,
        file: null,
      }));

      if (_.isEqual(converted, items)) return;

      setItems(converted);
    }, [initialFiles]);

    const { getRootProps, getInputProps, open, isDragAccept, fileRejections, acceptedFiles } =
      useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: isMultiple ? 2000 : 1,
        accept: formats,
        maxSize: maxSize ? maxSize * 1024 : undefined,
        disabled: disabled || items.length >= maxItems,
      });

    const handleOnChange = (fileList) => {
      setCurrentErrEnum([]);
      if (disabled) return;
      const incomingLength = fileList.length;
      const currentLength = items.length;
      const newLength = incomingLength + currentLength;
      if (newLength > maxItems) {
        const message = `Los archivos seleccionados rebasan el limite de ${maxItems} archivos permitidos`;
        setCurrentErrEnum([message]);
        return;
      }

      const currItems = fileList.map((file) => {
        return {
          file,
          wasSavedToServer: false,
          metadata: undefined,
        };
      });
      setItems((current) => {
        return [...current, ...currItems];
      });
    };

    const handleRejectedFiles = (rejectedListFiles) => {
      if (rejectedListFiles.length <= 0) return;
      const rejectedFilesErrors = rejectedListFiles.flatMap(({ file, errors = [] }) =>
        errors.map(({ code, message }) => ({
          code,
          message,
          name: file?.name || '',
        }))
      );

      const accumulatedString = rejectedFilesErrors.map(({ code, name }) => {
        const translatedMessage = errEnums[code] ?? code;
        return `${name}: ${translatedMessage}`;
      });

      setCurrentErrEnum(accumulatedString);
    };

    const handleOnChangeItemState = (state, index) => {
      setItems((current) => {
        return current.map((item, position) => {
          if (position !== index) return item;
          return state;
        });
      });
    };

    const handleOnSliceItemState = (index) => {
      setItems((current) => {
        return current.filter((_none, position) => position !== index);
      });
    };

    useEffect(() => {
      if (acceptedFiles === undefined || acceptedFiles.length < 1) return;
      handleOnChange(acceptedFiles);
    }, [acceptedFiles]);

    useEffect(() => {
      if (fileRejections === undefined) return;
      handleRejectedFiles(fileRejections);
    }, [fileRejections]);

    useEffect(() => {
      const filtered = items.filter((item) => item.wasSavedToServer).map((item) => item.metadata);
      onChange(filtered);
    }, [items]);

    useEffect(() => {
      if (currentErrEnum.length <= 0) return;
      console.warn(currentErrEnum);
      const message = currentErrEnum.join('\n');
      onMessage(message);
    }, [currentErrEnum]);

    useEffect(() => {
      return () => {
        setItems([]);
      };
    }, []);

    return useMemo(
      () => (
        <Card
          variant="outlined"
          className={clsx('border-dashed rounded-lg border-2 w-full relative', className)}
          ref={ref}
          {...props}
        >
          <CardContent
            className="overflow-hidden flex flex-col justify-center min-h-[50px]"
            style={{ padding: 0, margin: 0 }}
          >
            <Box
              className={clsx(
                'flex flex-row w-full px-24 justify-center sm:justify-start items-center flex-wrap sm:flex-nowrap bg-grey-100',
                'border-b-1'
              )}
            >
              {readOnly ? (
                <div className="flex flex-col justify-center mx-16 min-w-[150px] min-h-[52px] w-full text-center sm:text-start">
                  <Typography variant="inherit">
                    Archivos cargados: {items.filter((item) => item.wasSavedToServer).length}
                  </Typography>
                </div>
              ) : (
                <>
                  <div className="py-14">
                    <Button
                      className="whitespace-nowrap overflow-hidden text-ellipsis"
                      size="small"
                      onClick={open}
                      style={{ zIndex: 2000 }}
                      variant="contained"
                      color="primary"
                      disabled={disabled || items.length >= maxItems}
                      startIcon={<FileUploadOutlinedIcon />}
                    >
                      Subir desde computadora
                    </Button>
                  </div>
                  <div className="flex flex-col justify-center mx-16 min-w-[150px] w-full text-center sm:text-start">
                    <Typography variant="caption">
                      O arrasatra {isMultiple ? 'multiples archivos' : 'un archivo'} aquí
                    </Typography>
                  </div>
                </>
              )}
            </Box>

            {items.map((item, position) => (
              <ItemFile
                key={`${position}-itemload`}
                className={position + 1 < items.length ? 'border-b-1' : ''}
                setFile={item}
                position={position}
                onChangeState={(state) => handleOnChangeItemState(state, position)}
                onSlice={() => handleOnSliceItemState(position)}
                storeRequest={storeWrapper}
                deleteRequest={deleteWrapper}
                disabled={disabled || readOnly}
                {...itemOptions}
              />
            ))}

            <div
              {...getRootProps({ className: 'dropzone' })}
              className="absolute top-0 left-0 rounded-lg"
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: isDragAccept ? 'rgba(62, 30, 164, 0.25)' : undefined,
              }}
            >
              <input {...getInputProps()} />
            </div>
          </CardContent>
        </Card>
      ),
      [
        items,
        disabled,
        maxItems,
        isDragAccept,
        open,
        getRootProps,
        getInputProps,
        className,
        props,
        storeWrapper,
        deleteWrapper,
        itemOptions,
        isMultiple,
      ]
    );
  }
);
